// /tickets — staff support inbox. Filter bar (status / assigned /
// has-customer-reply) + table; row click → detail. Open tickets where
// the last reply came from the customer get a red dot — that's the
// "your turn" pile and where the operator's eye lands first.

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../lib/providers.js';
import {
  TICKETS_LIST_QUERY_KEY,
  ticketsApi,
  statusBadgeVariant,
  statusLabel,
  type ListTicketsParams,
  type TicketAssignedFilter,
  type TicketRow,
  type TicketStatusFilter,
} from '../../lib/api/tickets.js';
import { Badge } from '../../components/ui/badge.js';
import { Label } from '../../components/ui/label.js';
import { Select } from '../../components/ui/select.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import { EmptyState } from '../../components/ui/empty-state.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { formatDateTime } from '../../lib/format-date.js';

const STATUS_VALUES: TicketStatusFilter[] = [
  'all',
  'open',
  'in_progress',
  'waiting_customer',
  'resolved',
  'closed',
];
const ASSIGNED_VALUES: TicketAssignedFilter[] = ['all', 'me', 'unassigned'];

export function TicketsListPage() {
  const client = useApiClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: ListTicketsParams = useMemo(() => {
    return {
      status: (searchParams.get('status') as TicketStatusFilter | null) ?? 'all',
      assigned: (searchParams.get('assigned') as TicketAssignedFilter | null) ?? 'all',
      ...(searchParams.get('hasCustomerReply') === 'true' ? { hasCustomerReply: true } : {}),
      limit: 100,
    };
  }, [searchParams]);

  const query = useQuery({
    queryKey: [...TICKETS_LIST_QUERY_KEY, filters] as const,
    queryFn: () => ticketsApi.list(client, filters),
  });

  function setFilter(key: string, value: string | null): void {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === null || value.length === 0) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tickets"
        description="Staff inbox across all tenants. Open tickets with a customer reply at the tail are the ones that need your turn."
      />

      <div className="flex flex-col gap-3 rounded-lg border bg-card p-3 sm:flex-row sm:items-end">
        <FilterSelect
          label="Status"
          value={filters.status ?? 'all'}
          values={STATUS_VALUES}
          labelFor={(v) => (v === 'all' ? 'All statuses' : statusLabel(v))}
          onChange={(v) => {
            setFilter('status', v === 'all' ? null : v);
          }}
        />
        <FilterSelect
          label="Assigned"
          value={filters.assigned ?? 'all'}
          values={ASSIGNED_VALUES}
          labelFor={(v) => {
            switch (v) {
              case 'me':
                return 'Mine';
              case 'unassigned':
                return 'Unassigned';
              default:
                return 'Anyone';
            }
          }}
          onChange={(v) => {
            setFilter('assigned', v === 'all' ? null : v);
          }}
        />
        <div className="flex items-end gap-2 sm:ml-auto">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={filters.hasCustomerReply === true}
              onChange={(e) => {
                setFilter('hasCustomerReply', e.target.checked ? 'true' : null);
              }}
              className="h-4 w-4 rounded border-input"
            />
            Needs reply
          </label>
        </div>
      </div>

      {query.isPending ? (
        <TicketsTableSkeleton />
      ) : query.isError ? (
        <ErrorRetry
          title="Failed to load tickets"
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : query.data.rows.length === 0 ? (
        <EmptyState
          title="No tickets match the current filters"
          description="Try clearing the status / assigned filters."
        />
      ) : (
        <TicketsTable rows={query.data.rows} />
      )}
    </div>
  );
}

function FilterSelect<T extends string>({
  label,
  value,
  values,
  labelFor,
  onChange,
}: {
  label: string;
  value: T;
  values: readonly T[];
  labelFor: (v: T) => string;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5 sm:w-48">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select
        value={value}
        onChange={(e) => {
          onChange(e.target.value as T);
        }}
      >
        {values.map((v) => (
          <option key={v} value={v}>
            {labelFor(v)}
          </option>
        ))}
      </Select>
    </div>
  );
}

function TicketsTable({ rows }: { rows: readonly TicketRow[] }) {
  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-6"></TableHead>
            <TableHead>Subject</TableHead>
            <TableHead>Tenant</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Priority</TableHead>
            <TableHead>Last activity</TableHead>
            <TableHead>Assigned</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const needsReply = row.lastAuthorIsCustomer && row.status !== 'closed';
            return (
              <TableRow key={row.id} className="cursor-pointer hover:bg-muted/40">
                <TableCell className="px-2">
                  <Link to={`/tickets/${row.id}`} className="block">
                    <span
                      className={`inline-block h-2 w-2 rounded-full ${
                        needsReply ? 'bg-destructive' : 'bg-transparent'
                      }`}
                      aria-hidden
                      title={needsReply ? 'Customer replied — needs staff turn' : ''}
                    />
                  </Link>
                </TableCell>
                <TableCell className="max-w-[360px]">
                  <Link to={`/tickets/${row.id}`} className="block">
                    <div className="truncate font-medium">{row.subject}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      from {row.createdBy}
                    </div>
                  </Link>
                </TableCell>
                <TableCell className="text-sm">
                  <Link to={`/tenants/${row.tenantId}`} className="hover:text-foreground">
                    {row.tenantName}
                  </Link>
                </TableCell>
                <TableCell>
                  <Link to={`/tickets/${row.id}`} className="block">
                    <Badge variant={statusBadgeVariant(row.status)}>
                      {statusLabel(row.status)}
                    </Badge>
                  </Link>
                </TableCell>
                <TableCell>
                  <Link to={`/tickets/${row.id}`} className="block">
                    <span className="text-sm text-muted-foreground">{row.priority}</span>
                  </Link>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  <Link to={`/tickets/${row.id}`} className="block">
                    {formatDateTime(row.lastMessageAt)}
                  </Link>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  <Link to={`/tickets/${row.id}`} className="block">
                    {row.assignedStaffId ? (
                      <code className="text-xs">{row.assignedStaffId}</code>
                    ) : (
                      <span className="italic">unassigned</span>
                    )}
                  </Link>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function TicketsTableSkeleton() {
  return (
    <div className="space-y-2 rounded-lg border bg-card p-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}
